import { api, endpoints } from "../api.js";
import { useResource } from "../useResource.js";
import { Listening, type HistoryImported, type HistorySummary, type UnratedFavourite } from "./Listening.js";

/**
 * Listening import, wired.
 *
 * The file goes up as the request body rather than inside a JSON envelope. An extended history
 * file is tens of megabytes, and `JSON.stringify({ text })` would escape every quote in it —
 * a second copy in memory on the way out and a second parse on the way in, for nothing.
 */
export function ListeningScreen() {
  const status = useResource(() => endpoints.listeningStatus(), []);

  const send = async <T,>(url: string, file: File): Promise<T> => {
    const response = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: file,
    });
    if (!response.ok) {
      const detail = (await response.json().catch(() => ({}))) as { error?: string; detail?: string };
      throw new Error(
        detail.error === "unrecognised_file"
          ? `${file.name} is not a Spotify streaming-history file`
          : (detail.detail ?? `the server returned ${response.status}`),
      );
    }
    return (await response.json()) as T;
  };

  return (
    <Listening
      status={status.data ?? null}
      onPreview={(file) => send<HistorySummary>("/api/listening/preview", file)}
      onImport={(file) => send<HistoryImported>("/api/listening/import", file)}
      loadUnrated={async () =>
        (await api.get<{ songs: UnratedFavourite[] }>("/api/listening/unrated?limit=12")).songs
      }
    />
  );
}
